import React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "./PropertySummaryAccordions.css";

function formatDisplay(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function DetailField({ label, value }) {
  const str = value == null ? "" : String(value).trim();
  const isUrl = /^https?:\/\//i.test(str);

  return (
    <div className="property-summary-field">
      <span className="property-summary-field__label">{label}</span>
      {isUrl ? (
        <a
          className="property-summary-field__value property-summary-field__link"
          href={str}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open link
        </a>
      ) : (
        <span className="property-summary-field__value">
          {formatDisplay(value)}
        </span>
      )}
    </div>
  );
}

/**
 * Grouped accordions for read-only property facts (address, parcel, spaces, etc.).
 */
function PropertySummaryAccordions({ data }) {
  if (!data || typeof data !== "object") return null;

  const sections = [
    {
      id: "address",
      title: "Address & location",
      defaultExpanded: true,
      fields: [
        { label: "Street number", value: data.street_number },
        { label: "Street name", value: data.street_name },
        { label: "City", value: data.city },
        { label: "State / province", value: data.state },
        { label: "Country", value: data.country },
        { label: "ZIP / postal code", value: data.zip_code },
        { label: "County", value: data.county },
        { label: "Google Maps", value: data.google_maps_link },
      ],
    },
    {
      id: "parcel",
      title: "Parcel & subdivision",
      defaultExpanded: false,
      fields: [
        { label: "Subdivision", value: data.sub_division },
        { label: "Section", value: data.section },
        { label: "Block", value: data.block },
        { label: "Lot", value: data.lot },
        { label: "Plan number", value: data.plan_number },
        { label: "Floor plan type", value: data.floor_plan_type },
      ],
    },
    {
      id: "spaces",
      title: "Spaces & layout",
      defaultExpanded: false,
      fields: [
        { label: "Living area (SF)", value: data.living_sq_ft },
        { label: "Total area (SF)", value: data.total_sq_ft },
        { label: "Stories", value: data.stories },
        { label: "Bedrooms", value: data.bedrooms },
        { label: "Bathrooms", value: data.bathrooms },
        { label: "Office / studios", value: data.offices_studios },
        { label: "Media room", value: data.media_room },
        { label: "Game room", value: data.game_room },
        { label: "Casita", value: data.casita },
      ],
    },
    {
      id: "site",
      title: "Site & outdoor",
      defaultExpanded: false,
      fields: [
        { label: "Pools", value: data.pools },
        { label: "Garages", value: data.garages },
      ],
    },
    {
      id: "utilities",
      title: "Utility accounts",
      defaultExpanded: false,
      fields: [
        { label: "Water — account name", value: data.water_account_name },
        { label: "Water — account number", value: data.water_account_number },
        {
          label: "Electricity — account name",
          value: data.electricity_account_name,
        },
        {
          label: "Electricity — account number",
          value: data.electricity_account_number,
        },
        { label: "Gas — account name", value: data.gas_account_name },
        { label: "Gas — account number", value: data.gas_account_number },
      ],
    },
  ];

  return (
    <div className="property-summary-accordions">
      {sections.map((section) => (
        <Accordion
          key={section.id}
          defaultExpanded={section.defaultExpanded}
          className="property-summary-accordions__item"
          disableGutters
          elevation={0}
        >
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon className="property-summary-accordions__expand-icon" />
            }
            className="property-summary-accordions__summary"
          >
            <span className="property-summary-accordions__title">
              {section.title}
            </span>
          </AccordionSummary>
          <AccordionDetails className="property-summary-accordions__details">
            <div className="property-summary-accordions__grid">
              {section.fields.map((f) => (
                <DetailField key={f.label} label={f.label} value={f.value} />
              ))}
            </div>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
}

export default PropertySummaryAccordions;
